import { useState, useEffect } from 'react';
import { LogIn, UserCheck, ShieldCheck, LogOut, Zap, ArrowRight, Loader2 } from 'lucide-react';
import { authService, supabase } from '../lib/supabase';

interface LandingViewProps {
  onContinueAsGuest: () => void;
  onOpenAuth: () => void;
  onNavigateTab: (tabId: string) => void;
}

export function LandingView({ onContinueAsGuest, onOpenAuth, onNavigateTab }: LandingViewProps) {
  const [user, setUser] = useState<any>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Check existing session from local storage immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted) {
        setUser(session?.user || null);
        setLoadingUser(false);
      }
    });

    // Also get current user asynchronously as double-check
    authService.getCurrentUser().then(u => {
      if (isMounted) {
        setUser(u);
        setLoadingUser(false);
      }
    });

    // Listen for auth state changes (callback receives user object)
    const { data: { subscription } } = authService.onAuthStateChange((u) => {
      if (isMounted) {
        setUser(u);
        setLoadingUser(false);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    await authService.signOut();
    setUser(null);
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
      <div className="w-full max-w-lg relative rounded-3xl overflow-hidden border border-purple-500/30 bg-gradient-to-b from-[#0f0e26] via-[#0a081a] to-[#05040d] p-8 sm:p-10 text-center shadow-2xl shadow-purple-950/60">
        {/* Glow Accents */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-80 h-80 bg-purple-600/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-1/2 right-4 w-60 h-60 bg-amber-500/15 blur-[100px] rounded-full pointer-events-none" />

        {/* Mascot & Logo */}
        <div className="relative z-10 flex flex-col items-center mb-6">
          <video
            src="/jud.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="h-20 w-20 sm:h-24 sm:w-24 object-contain mix-blend-lighten filter drop-shadow-[0_0_20px_rgba(168,85,247,0.4)] mb-1"
            aria-label="DECKSTR mascot"
          />
          <img
            src="/deckstr-logo.png"
            alt="DECKSTR.GG"
            className="h-10 sm:h-12 object-contain"
          />
        </div>

        {/* Prompt Header */}
        <div className="relative z-10 mb-8">
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-2">
            {user ? 'Welcome Back!' : 'Sign In or Continue as Guest'}
          </h1>
          <p className="text-sm text-slate-300/80 max-w-sm mx-auto leading-relaxed">
            {user
              ? `You are signed in as ${user.user_metadata?.display_name || user.email?.split('@')[0]}. Continue to DECKSTR feed or sign out.`
              : 'Welcome to DECKSTR.GG. Please sign in to save your decks and track events, or continue as guest to enter the site.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="relative z-10 space-y-3 max-w-xs mx-auto">
          {loadingUser ? (
            <div className="flex items-center justify-center py-6 text-white/50 gap-2">
              <Loader2 className="size-5 animate-spin text-amber-400" />
              <span>Checking session...</span>
            </div>
          ) : user ? (
            /* IF ALREADY SIGNED IN UPON RELOAD */
            <>
              <button
                onClick={() => onNavigateTab('news')}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-500 via-amber-400 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold text-base rounded-xl transition-all shadow-lg shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <Zap className="size-5 fill-current" />
                Continue Signed In
                <ArrowRight className="size-5" />
              </button>

              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-slate-800/90 hover:bg-slate-800 text-red-400 border border-red-500/30 font-semibold text-base rounded-xl transition-all hover:border-red-500/60 hover:scale-[1.02] active:scale-[0.98]"
              >
                <LogOut className="size-5" />
                Sign Out
              </button>
            </>
          ) : (
            /* IF NOT SIGNED IN */
            <>
              <button
                onClick={onOpenAuth}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-500 via-amber-400 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold text-base rounded-xl transition-all shadow-lg shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <LogIn className="size-5" />
                Sign In
              </button>

              <button
                onClick={onContinueAsGuest}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-slate-800/90 hover:bg-slate-800 text-slate-100 border border-slate-700 font-semibold text-base rounded-xl transition-all hover:border-slate-500 hover:scale-[1.02] active:scale-[0.98]"
              >
                <UserCheck className="size-5 text-cyan-400" />
                Continue as Guest
              </button>
            </>
          )}
        </div>

        {/* Note */}
        <div className="relative z-10 mt-6 pt-4 border-t border-white/5 text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="size-4 text-emerald-400" />
          <span>Guest mode gives instant access to all tools & probability analytics.</span>
        </div>
      </div>
    </div>
  );
}
